import React from 'react';
import {
    Box,
    Paper,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Chip,
    CircularProgress,
    Button,
    Alert,
    Tooltip,
    alpha,
} from '@mui/material';
import {
    Delete as DeleteIcon,
    Devices as DevicesIcon,
    Computer as ComputerIcon,
    Smartphone as SmartphoneIcon,
    Tablet as TabletIcon,
    LogoutRounded as LogoutAllIcon,
} from '@mui/icons-material';
import { PageHeader } from '../../components/admin';
import {
    useSessionsQuery,
    useDeleteSessionMutation,
    useLogoutAllMutation,
} from '../../api/JsdhNotifikaceService/service/AuthMutation';

const getDeviceIcon = (deviceInfo: string) => {
    const lower = deviceInfo.toLowerCase();
    if (lower.includes('mobile') || lower.includes('phone') || lower.includes('android') || lower.includes('iphone')) {
        return <SmartphoneIcon />;
    }
    if (lower.includes('tablet') || lower.includes('ipad')) {
        return <TabletIcon />;
    }
    return <ComputerIcon />;
};

const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('cs-CZ', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
};

const SessionsPage: React.FC = () => {
    const { data: sessions, isLoading, error } = useSessionsQuery();
    const deleteSessionMutation = useDeleteSessionMutation();
    const logoutAllMutation = useLogoutAllMutation();

    const handleDeleteSession = (sessionId: number) => {
        deleteSessionMutation.mutate(sessionId);
    };

    const handleLogoutAll = () => {
        if (window.confirm('Opravdu chcete ukončit všechny sessions? Budete odhlášeni ze všech zařízení.')) {
            logoutAllMutation.mutate();
        }
    };

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Alert severity="error">
                Nepodařilo se načíst seznam sessions: {error.message}
            </Alert>
        );
    }

    return (
        <Box>
            <PageHeader
                title="Aktivní sessions"
                icon={<DevicesIcon />}
                action={
                    <Button
                        variant="outlined"
                        color="error"
                        startIcon={<LogoutAllIcon />}
                        onClick={handleLogoutAll}
                        disabled={logoutAllMutation.isPending || !sessions?.length}
                    >
                        Odhlásit ze všech zařízení
                    </Button>
                }
            />

            <Alert severity="info" sx={{ mb: 3 }}>
                Zde můžete spravovat vaše aktivní přihlášení. Aktuální session je označena zeleně.
                Ukončením session se odhlásíte z daného zařízení.
            </Alert>

            <Paper>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Zařízení</TableCell>
                                <TableCell>IP adresa</TableCell>
                                <TableCell>Přihlášení</TableCell>
                                <TableCell>Platnost do</TableCell>
                                <TableCell align="right">Akce</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sessions && sessions.length > 0 ? (
                                sessions.map((session) => (
                                    <TableRow
                                        key={session.id}
                                        sx={(theme) => (session.current ? {
                                            backgroundColor: alpha(theme.palette.success.main, 0.08),
                                            borderLeft: `4px solid ${theme.palette.success.main}`,
                                            '& > td:first-of-type': {
                                                paddingLeft: 'calc(16px - 4px)',
                                            },
                                        } : {})}
                                    >
                                        <TableCell>
                                            <Box display="flex" alignItems="center" gap={1}>
                                                {getDeviceIcon(session.deviceInfo)}
                                                <Box>
                                                    <Typography variant="body2">
                                                        {session.deviceInfo || 'Neznámé zařízení'}
                                                    </Typography>
                                                    {session.current && (
                                                        <Chip
                                                            label="Aktuální"
                                                            color="success"
                                                            size="small"
                                                            sx={{ mt: 0.5 }}
                                                        />
                                                    )}
                                                </Box>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" fontFamily="monospace">
                                                {session.ipAddress || '-'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            {formatDate(session.createdAt)}
                                        </TableCell>
                                        <TableCell>
                                            {formatDate(session.expiresAt)}
                                        </TableCell>
                                        <TableCell align="right">
                                            {!session.current && (
                                                <Tooltip title="Ukončit session">
                                                    <IconButton
                                                        color="error"
                                                        onClick={() => handleDeleteSession(session.id)}
                                                        disabled={deleteSessionMutation.isPending}
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={5} align="center">
                                        <Typography color="text.secondary">
                                            Žádné aktivní sessions
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            <Paper sx={{ p: 3, mt: 3 }}>
                <Typography variant="h6" gutterBottom>
                    O sessions
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                    Každé přihlášení vytvoří novou session, která je platná do data zobrazeného ve sloupci „Platnost do" (typicky 7 dní od přihlášení, konkrétní doba je dána konfigurací serveru).
                    Po vypršení platnosti budete automaticky odhlášeni a budete se muset přihlásit znovu.
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Maximální počet současných sessions je 10. Při překročení limitu bude nejstarší session automaticky ukončena.
                </Typography>
            </Paper>
        </Box>
    );
};

export default SessionsPage;
